import React, { useEffect, useState, useContext } from 'react';
import { NavContext } from '@/components/NavContext';
import { AuthContext } from '@/components/Contexts/AuthContext';
import LearningPathHeader from './LearningPathHeader';
import EditLearningPathHomepage from './EditLearningPathHomepage';
import EditSectionLearningPath from './EditSectionLearningPath';
import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import PublishLearningPath from '@/components/LearningPath/PublishLearningPath';
import { nthLastIndexOf, parseFilter } from '@/util';
import { VersionLPPublication } from '@/components/LearningPath/VersionLPPublication';
import { ContentCreatorInviteLP } from '@/components/LearningPath/ContentCreatorInviteLP';
import { MisingConnect } from '@/components/LearningPath/MisingConnect';

function LearningPath() {
  const { setNavDisabled } = useContext(NavContext);
  const { auth } = useContext(AuthContext);
  const [activeEdit, setActiveEdit] = useState(false);
  const { learningpathId }: any = useParams();

  const [image, setImage] = useState<string>('/assets/images/LearnPath.png');
  const [learningpath, setLearningpath] = useState<any>('');
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isOpenPublish, setIsOpenPublish] = useState(false);
  const [isPiblished, setIsPiblished] = useState(false);
  const [isOpenVersionModal, setIsOpenVersionModal] = useState(false);
  const [contentCreators, setContentCreators] = useState<any>(null);
  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);
  const [allNodes, setAllNodes] = useState<any>(null);

  const [isMisingConnect, setIsMisingConnect] = useState(false);
  const [misingConnect, setMisingConnect] = useState<any>(null);

  const [isOwner, setIsOwner] = useState<any>(false);

  const authUser = auth?.user;
  const authOrg: any = auth?.user?.activeOrganisationId;
  const authLang: any = auth?.user.lang;
  const navigate = useNavigate();

  const [lpCompetencies, setLpCompetencies] = useState<any>([]);
  const [newLpCompetencies, setNewLpCompetencies] = useState<any>([]);

  useGet(`/categories`, {
    sort: 'tag',
    lang: auth?.user?.lang,
    organisationId: `${auth?.user?.activeOrganisationId},OR,organisationId=null`,
    onSuccess: (result: any) => {
      setAllCategories(
        result?.data?.map((el: any) => ({
          id: el.id,
          label: el.tag,
          icon: el.icon,
        })) || []
      );
      setCategories(
        result?.data
          ?.filter((el: any) => el?.lang === authUser?.lang)
          ?.map((el: any) => ({
            id: el.id,
            label: el.tag,
            icon: el.icon,
          })) || []
      );
    },
  });

  useGet(`/learningpath/${learningpathId}`, {
    retry: false,
    onSuccess: (res: any) => {
      setLearningpath(res?.data || undefined);
    },
  });

  const { refetch: refetchCreatorResults }: any = useGet(`/lpcontentcreators`, {
    learningpathId,
    onSuccess: (result: any) => {
      setContentCreators(result?.data || null);
    },
  });

  const { refetch: refetchCorrection } = useGet(`/learningpathnodedetails`, {
    learningpathId: learningpathId,
    organisationId: authOrg,
    onSuccess: async (res: any) => {
      setAllNodes(res?.data);
    },
  });

  const { refetch: refetchCompetencies } = useGet(
    `/learningpathcompetencedetails`,
    {
      learningpathId: learningpathId,
      onSuccess: (result: any) => {
        setLpCompetencies(result?.data || []);
        setNewLpCompetencies(result?.data || []);
      },
    }
  );

  useEffect(() => {
    if (contentCreators) {
      const myUser = contentCreators?.find(
        (user: any) => user?.id === authUser?.id
      );
      if (
        (myUser && myUser?.role >= 90) ||
        (authUser?.activeOrganisationRole || 0) >= 90
      ) {
        setIsOwner(true);
      } else {
        setIsOwner(false);
      }
    }
  }, [contentCreators]);

  useEffect(() => {
    if (learningpath) {
      learningpath?.lpPicture && setImage(learningpath?.lpPicture);
    }
  }, [learningpath]);

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  }, []);

  useEffect(() => {
    if (isOpenPublish) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpenPublish]);

  useEffect(() => {
    const redirectPath = window.location.pathname.slice(
      0,
      nthLastIndexOf(window.location.pathname, '/', 1)
    );
    if (isPiblished) {
      navigate(redirectPath);
    }
  }, [isPiblished]);

  if (!learningpath || !allNodes) return null;

  return (
    <div className="flex max-h-[calc(100vh_-_70px)] min-h-[calc(100vh_-_70px)] flex-col">
      <LearningPathHeader
        learningpathId={learningpathId}
        learningpath={learningpath}
        setLearningpath={setLearningpath}
        setIsOpenPublish={setIsOpenPublish}
        setIsOpenVersionModal={setIsOpenVersionModal}
        contentCreators={contentCreators}
        setIsOpenInviteModal={setIsOpenInviteModal}
        isOwner={isOwner}
        authOrg={authOrg}
        setIsMisingConnect={setIsMisingConnect}
        setMisingConnect={setMisingConnect}
      />
      {activeEdit || allNodes?.length > 1 ? (
        <EditSectionLearningPath
          learningpathId={learningpathId}
          learningpath={learningpath}
          setImage={setImage}
          image={image}
          allNodes={allNodes}
          categories={categories}
          allCategories={allCategories}
          setLearningpath={setLearningpath}
          isOwner={isOwner}
          refetchCorrection={refetchCorrection}
          misingConnect={misingConnect}
          setMisingConnect={setMisingConnect}
          setNewLpCompetencies={setNewLpCompetencies}
          newLpCompetencies={newLpCompetencies}
          setLpCompetencies={setLpCompetencies}
          lpCompetencies={lpCompetencies}
          refetchCompetencies={refetchCompetencies}
        />
      ) : (
        <EditLearningPathHomepage
          setActiveEdit={setActiveEdit}
          learningpathId={learningpathId}
          learningpath={learningpath}
          setLearningpath={setLearningpath}
          setImage={setImage}
          image={image}
          isOwner={isOwner}
          authLang={authLang}
          setNewLpCompetencies={setNewLpCompetencies}
          newLpCompetencies={newLpCompetencies}
          setLpCompetencies={setLpCompetencies}
          lpCompetencies={lpCompetencies}
          refetchCompetencies={refetchCompetencies}
        />
      )}
      {isOpenPublish && (
        <PublishLearningPath
          learningpath={learningpath}
          setIsOpenPublish={setIsOpenPublish}
          setIsPiblished={setIsPiblished}
          learningpathId={learningpathId}
        />
      )}
      {isMisingConnect && (
        <MisingConnect
          closeModal={() => setIsMisingConnect(false)}
          misingConnect={misingConnect}
          allCategories={allCategories}
        />
      )}
      {isOpenVersionModal && (
        <VersionLPPublication
          closeModal={() => setIsOpenVersionModal(false)}
          learningpath={learningpath}
          setIsOpenVersionModal={setIsOpenVersionModal}
        />
      )}

      {isOpenInviteModal && (
        <ContentCreatorInviteLP
          onClose={() => setIsOpenInviteModal(false)}
          learningpath={learningpath}
          refetchCreatorResults={refetchCreatorResults}
          setLearningpath={setLearningpath}
          contentCreators={contentCreators}
        />
      )}
    </div>
  );
}

export default LearningPath;
